import React, { useState, useEffect } from 'react';
import { fetchSentiment, fetchArticles } from '../../hooks/useMarketIntelligence';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

const DIRECTION_STYLES = {
  bullish: { bar: 'bg-green-500', pill: 'text-green-300 border-green-700/50 bg-green-900/40' },
  bearish: { bar: 'bg-red-500', pill: 'text-red-300 border-red-700/50 bg-red-900/40' },
  neutral: { bar: 'bg-slate-500', pill: 'text-slate-400 border-slate-600 bg-slate-700/60' },
};

/**
 * Compact sentiment + news panel for the token list from a research result.
 * Props:
 *   tokens      — string[] of proposed token symbols
 *   apiBaseUrl  — base URL for market-intelligence proxy
 */
export default function ResearchTokenIntelligence({ tokens, apiBaseUrl }) {
  const [sentiment, setSentiment] = useState(null);
  const [articles, setArticles] = useState(null);
  const [sentimentLoading, setSentimentLoading] = useState(false);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [sentimentError, setSentimentError] = useState(null);
  const [articlesError, setArticlesError] = useState(null);

  const tokenParam = tokens.join(',');

  useEffect(() => {
    if (!tokens.length) return;
    setSentimentLoading(true);
    setSentimentError(null);
    fetchSentiment(apiBaseUrl, { tokens: tokenParam, time_window: '24h' })
      .then(setSentiment)
      .catch(setSentimentError)
      .finally(() => setSentimentLoading(false));
  }, [apiBaseUrl, tokenParam]);

  useEffect(() => {
    if (!tokens.length) return;
    setArticlesLoading(true);
    setArticlesError(null);
    fetchArticles(apiBaseUrl, { tokens: tokenParam, min_impact: 0.3, limit: 8, time_window: '24h' })
      .then(setArticles)
      .catch(setArticlesError)
      .finally(() => setArticlesLoading(false));
  }, [apiBaseUrl, tokenParam]);

  if (!tokens.length) return null;

  const Spinner = () => (
    <svg className="animate-spin h-4 w-4 text-cyan-400" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );

  return (
    <div className="space-y-4">
      {/* Sentiment */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Token Sentiment
          <span className="ml-auto text-xs text-slate-500 font-normal">24h</span>
        </h4>

        {sentimentLoading && <div className="flex justify-center py-4"><Spinner /></div>}
        {sentimentError && <p className="text-xs text-slate-500 py-2 text-center">Sentiment data unavailable</p>}
        {sentiment && (
          <div className="space-y-2">
            {sentiment.tokens?.length === 0 && (
              <p className="text-xs text-slate-500 py-2 text-center">No sentiment data for these tokens</p>
            )}
            {sentiment.tokens?.map((token) => {
              const styles = DIRECTION_STYLES[token.sentiment_direction] || DIRECTION_STYLES.neutral;
              const barWidth = Math.round(((token.sentiment_score + 1) / 2) * 100);
              return (
                <div key={token.token_symbol} className="flex items-center gap-3">
                  <span className="w-14 text-sm font-bold text-white shrink-0">{token.token_symbol}</span>
                  <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${styles.bar}`} style={{ width: `${barWidth}%` }} />
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${styles.pill} shrink-0`}>
                    {token.sentiment_direction}
                  </span>
                  <span className="text-xs text-slate-500 w-12 text-right shrink-0">
                    {token.mention_count} art.
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent headlines */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <div className="flex items-start gap-2 mb-3">
          <svg className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <h4 className="text-sm font-semibold text-white">Recent News</h4>
              <span className="text-xs text-slate-500">24h</span>
            </div>
            {/* Token filter chips */}
            <div className="flex flex-wrap gap-1">
              {tokens.map((t) => (
                <span key={t} className="px-1.5 py-0.5 rounded text-xs font-medium bg-cyan-900/40 border border-cyan-700/50 text-cyan-300">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {articlesLoading && <div className="flex justify-center py-4"><Spinner /></div>}
        {articlesError && <p className="text-xs text-slate-500 py-2 text-center">News data unavailable</p>}
        {articles && (
          <div className="space-y-3 mt-1">
            {articles.articles?.length === 0 && (
              <p className="text-xs text-slate-500 py-2 text-center">No recent news for these tokens</p>
            )}
            {articles.articles?.map((article, idx) => {
              const dir = article.sentiment?.direction || article.sentiment_direction || 'neutral';
              const pill = DIRECTION_STYLES[dir]?.pill || DIRECTION_STYLES.neutral.pill;
              const tokenSet = new Set(tokens.map((t) => t.toUpperCase()));
              const allMentions = article.token_symbols || article.token_mentions || [];
              const matched = allMentions.filter((t) => tokenSet.has(t.toUpperCase()));
              const others = allMentions.filter((t) => !tokenSet.has(t.toUpperCase()));
              return (
                <div key={idx} className="border-b border-slate-700/50 last:border-0 pb-3 last:pb-0">
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-400 hover:text-cyan-300 underline leading-snug block mb-1.5"
                  >
                    {article.title || article.headline}
                  </a>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Matched tokens — highlighted */}
                    {matched.map((t) => (
                      <span key={t} className="px-1.5 py-0.5 rounded text-xs font-semibold bg-cyan-900/40 border border-cyan-700/50 text-cyan-300">
                        {t}
                      </span>
                    ))}
                    {/* Other mentioned tokens — muted */}
                    {others.slice(0, 3).map((t) => (
                      <span key={t} className="px-1.5 py-0.5 rounded text-xs bg-slate-700/40 border border-slate-600/50 text-slate-500">
                        {t}
                      </span>
                    ))}
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${pill}`}>{dir}</span>
                    <span className="text-xs text-slate-500 ml-auto">{timeAgo(article.published_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
